import React, { useEffect, useState } from "react";
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

import logo from "../../util/pdf/assets/cystellarPdfLogo.png";
import bannerImage from "../../util/pdf/assets/Reale-ClaimPDFBanner-1200x260.png";
import { MapImagePdf } from "../../util/pdf/MapImagePdf";
import { RiskEvaluationTable } from "../../util/pdf/RiskEvaluationTable";
import { formatDateValue } from "../../util/tableUtils";
import { pdfPageCss } from "../../util/pdf/pdfCssStyles";
import { HazardIdEnum } from "../hazardIdEnum";
import { RiskColorsLegend } from "../../util/pdf/RiskColorsLegend";

const styles = StyleSheet.create(pdfPageCss);

export const RiskAssessmentsPdf = ({ tableRisksData, latitude, longitude, renderDescription }) => {
  const { t } = useTranslation();

  const [firstSection, setFirstSection] = useState([]);
  const [secondSection, setSecondSection] = useState([]);
  const [thirdSection, setThirdSection] = useState([]);
  const [forthSection, setForthSection] = useState([]);
  const [expiredSection, setExpiredSection] = useState([]);

  useEffect(() => {
    if (tableRisksData.length === 0) return;

    const firstSectionIds = [
      HazardIdEnum.SeismicRiskHowden2,
      HazardIdEnum.InlandFloodHowden56,
      HazardIdEnum.LandslideRiskIspra,
      HazardIdEnum.SnowIta30cmANIA,
      HazardIdEnum.RealeGroupHail,
      HazardIdEnum.RealeGroupWind,
    ];
    const first = tableRisksData.filter((risk) =>
      firstSectionIds.some((firstRisk) => risk.cysId === firstRisk),
    );
    setFirstSection(first);

    const secondSectionIds = [
      HazardIdEnum.WindItaANIA,
      HazardIdEnum.HailItaANIA,
      HazardIdEnum.LightningStrikeItaANIA,
      HazardIdEnum.PrecipitationIta75mmANIA,
    ];
    const second = tableRisksData.filter((risk) =>
      secondSectionIds.some((secondRisk) => risk.cysId === secondRisk),
    );
    setSecondSection(second);

    const thirdSectionIds = [
      HazardIdEnum.SnowIta25cm,
      HazardIdEnum.LightningStrikeIta,
      HazardIdEnum.SeismicClassificationIta,
      // Current 2025 inland flood
      HazardIdEnum.InlandFloodIta10y,
      HazardIdEnum.InlandFloodIta20y,
      HazardIdEnum.InlandFloodIta50y,
      HazardIdEnum.InlandFloodIta100y,
      HazardIdEnum.InlandFloodIta500y,

      HazardIdEnum.CoastalFlood10y,
      HazardIdEnum.CoastalFlood30y,
      HazardIdEnum.CoastalFlood100y,
      HazardIdEnum.CoastalFlood300y,
      HazardIdEnum.CoastalFlood1000y,
      HazardIdEnum.FlashFloodIta3h,
      HazardIdEnum.FlashFloodIta24h,
      HazardIdEnum.FlashFloodIta48h,
      HazardIdEnum.FlashFloodIta72h,
      HazardIdEnum.HailItaLarge,
      HazardIdEnum.HailItaExLarge,
    ];
    const third = tableRisksData.filter((risk) =>
      thirdSectionIds.some((thirdRisk) => risk.cysId === thirdRisk),
    );
    setThirdSection(third);

    const forthSectionIds = [
      HazardIdEnum.HighWindItaExtreme,
      HazardIdEnum.HighWindItaSevere,
      HazardIdEnum.SnowIta5cm,
    ];
    const forth = tableRisksData.filter((risk) =>
      forthSectionIds.some((forthRisk) => risk.cysId === forthRisk),
    );
    setForthSection(forth);

    const expiredMapsIds = [
      HazardIdEnum.SeismicRiskHowden,
      HazardIdEnum.InlandFloodHowden,
      HazardIdEnum.InlandFloodHowden58,
    ];

    const expired = tableRisksData.filter((risk) =>
      expiredMapsIds.some((expiredRisk) => risk.cysId === expiredRisk),
    );
    setExpiredSection(expired);
  }, [tableRisksData]);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.tableAndLegendContainer}>
          <View fixed style={styles.header}>
            <Image style={styles.headerImage} src={logo} />
          </View>
          <Image style={styles.bannerImage} src={bannerImage} />
          <Text style={[styles.title, styles.boldText]}>
            Valutazione climatica {formatDateValue(new Date())}
          </Text>

          <View style={styles.container}>
            {/* this is used for policy/location description */}
            {renderDescription()}

            <View style={styles.section}>
              <MapImagePdf latitude={latitude} longitude={longitude} />
            </View>
          </View>
          <Text style={styles.centeredTitle}>{`${t("menu.riskEvaluation")}:`}</Text>
          <View style={styles.line} />
          <Text style={styles.title}>{"MAPPE DI RISCHIO CATASTROFALI E ATMOSFERICI:"}</Text>
          <View style={styles.section}>
            <RiskEvaluationTable tableRisksData={firstSection}></RiskEvaluationTable>
          </View>
        </View>
      </Page>

      <Page size="A4" style={styles.page}>
        <View style={styles.tableAndLegendContainer}>
          <Text style={styles.title}>{"ALTRE MAPPE DI RISCHIO PREFERITE:"}</Text>
          <View style={styles.section}>
            <RiskEvaluationTable tableRisksData={secondSection}></RiskEvaluationTable>
          </View>

          <View style={styles.addSpace} />

          <Text style={styles.title}>{"ALTRE MAPPE DI RISCHIO:"}</Text>
          <View style={styles.section}>
            <RiskEvaluationTable tableRisksData={thirdSection}></RiskEvaluationTable>
          </View>
        </View>
      </Page>

      <Page size="A4" style={styles.page}>
        <View style={styles.tableAndLegendContainer}>
          <View>
            <RiskEvaluationTable tableRisksData={forthSection}></RiskEvaluationTable>
          </View>

          <View style={styles.addSpace} />

          <Text style={styles.title}>
            {"MAPPE DI RISCHIO CATASTROFALI E ATMOSFERICI NON PIU' IN VIGORE:"}
          </Text>
          <View style={styles.section}>
            <RiskEvaluationTable tableRisksData={expiredSection}></RiskEvaluationTable>
          </View>
          <View style={styles.section} wrap={false}>
            <RiskColorsLegend />
          </View>
        </View>
      </Page>
    </Document>
  );
};
